/*
 * "My Follow-Ups Due Today" — agent dashboard widget.
 *
 * Shows the ranked shortlist (max 8) of revivable calls the CURRENT
 * agent should call back today. Backend `/api/analytics/my-follow-ups-due`
 * (see routes/analytics.py::get_my_follow_ups_due) does the ranking so
 * this component is a thin renderer — pill-band on the left tells the
 * agent WHY the row is on the list, the phone + short reason on the
 * right, and clicking anywhere on the row opens the call detail page.
 *
 * Design rules:
 *   • Empty state stays quiet — "You're all caught up" green. Agents
 *     will visit this every morning; no scary red badges when the list
 *     is clean.
 *   • Reassignment is never done client-side — this widget is READ ONLY
 *     with a "Mark called" pass-through to `/analytics/revival/{call_id}
 *     /mark-contacted`. Only Narin/admins can reassign.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PhoneCall, Loader2, CheckCircle2, Zap, Snowflake, UserCheck, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PlaybookModal } from './RevivableDealQueue';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_META = {
  manager_reassigned: {
    icon: UserCheck,
    label: 'Reassigned',
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', accent: 'bg-amber-500',
  },
  cooling_hot_lead: {
    icon: Zap,
    label: 'Hot & cooling',
    bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', accent: 'bg-rose-500',
  },
  stale_warm: {
    icon: Snowflake,
    label: 'Warm follow-up',
    bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', accent: 'bg-sky-500',
  },
};

const _fmtPhone = (raw) => {
  const s = String(raw || '').trim();
  if (/^61\d{9,10}$/.test(s)) return `+${s.slice(0, 2)} ${s.slice(2, 5)} ${s.slice(5, 8)} ${s.slice(8)}`;
  return s;
};

export const MyFollowUpsDueCard = ({ domainId = null }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);
  const [playbookFor, setPlaybookFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/analytics/my-follow-ups-due${domainId ? `?domain_id=${domainId}` : ''}`;
      const r = await axios.get(url, { timeout: 15000 });
      setRows(r.data?.rows || []);
    } catch (e) {
      // Fail quiet — this is a dashboard widget, not critical path
      console.warn('follow-ups fetch failed', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => { load(); }, [load]);

  const markContacted = async (callId, e) => {
    e.stopPropagation();
    if (markingId) return;
    setMarkingId(callId);
    try {
      await axios.post(`${API}/analytics/revival/${callId}/mark-contacted`);
      toast.success('Marked as called — nice work.');
      // Optimistic remove
      setRows(prev => prev.filter(r => r.call_id !== callId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mark as called');
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="my-follow-ups-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-teal-600" />
            My Follow-Ups Due Today
          </CardTitle>
          <span className="text-[11px] text-slate-500 font-mono" data-testid="follow-ups-count">
            {loading ? '' : `${rows.length} to call`}
          </span>
        </div>
        <p className="text-[11px] text-slate-500">
          Ranked by urgency — hot deals cooling, manager reassigns, and warm follow-ups you own.
          Reassignments are supervisor-only; this list only shows deals allocated to you.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your queue…
          </div>
        ) : rows.length === 0 ? (
          <div
            className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700"
            data-testid="follow-ups-empty"
          >
            <CheckCircle2 className="w-4 h-4" /> You&apos;re all caught up — no follow-ups due today.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((row) => {
              const meta = BAND_META[row.band] || BAND_META.stale_warm;
              const Icon = meta.icon;
              return (
                <li
                  key={row.call_id}
                  className={`group flex items-stretch gap-3 px-1 py-2.5 cursor-pointer transition-colors hover:${meta.bg}`}
                  onClick={() => navigate(`/calls/${row.call_id}`)}
                  data-testid={`follow-up-row-${row.call_id}`}
                >
                  <div className={`w-1 rounded-full ${meta.accent}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${meta.bg} ${meta.border} border ${meta.text}`}
                        title={row.label}
                      >
                        <Icon className="w-2.5 h-2.5" /> {meta.label}
                      </span>
                      <span className="text-sm font-semibold text-slate-800 tabular-nums">
                        {_fmtPhone(row.customer_phone)}
                      </span>
                      {row.caller_disc && (
                        <span className="text-[10px] font-bold text-slate-400">DISC · {row.caller_disc}</span>
                      )}
                      <span className="ml-auto text-[10px] text-slate-400">{row.label}</span>
                    </div>
                    {row.recommended_next_action && (
                      <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                        <span className="font-medium text-slate-600">Next: </span>
                        {row.recommended_next_action}
                      </p>
                    )}
                  </div>
                  <div className="self-center flex items-center gap-2.5 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setPlaybookFor(row); }}
                      className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md bg-gradient-to-br from-teal-600 to-teal-700 hover:from-teal-500 hover:to-teal-600 text-white shadow-sm transition-all"
                      data-testid={`follow-up-playbook-${row.call_id}`}
                      title="Open a callback playbook: opening line, checklist and points to cover"
                    >
                      <BookOpen className="w-3 h-3" /> Playbook
                    </button>
                    <button
                      onClick={(e) => markContacted(row.call_id, e)}
                      disabled={markingId === row.call_id}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-800 disabled:opacity-40 transition-colors"
                      data-testid={`follow-up-mark-called-${row.call_id}`}
                      title="Mark this follow-up as called"
                    >
                      {markingId === row.call_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3 h-3" />
                      )}
                      Called
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      {playbookFor && (
        <PlaybookModal row={playbookFor} onClose={() => setPlaybookFor(null)} />
      )}
    </Card>
  );
};

export default MyFollowUpsDueCard;
